import { AlertCircle, Wifi, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApiRetryButton } from '@/components/RetryButton';
import { getErrorMessage, isNetworkError, isAuthenticationError } from '@/lib/errors';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

interface CorpusErrorDisplayProps {
  error: unknown;
  onRetry: () => Promise<void>;
  className?: string;
}

export function CorpusErrorDisplay({ error, onRetry, className }: CorpusErrorDisplayProps) {
  const networkStatus = useNetworkStatus();
  const errorMessage = getErrorMessage(error);
  const isNetwork = isNetworkError(error);
  const isAuth = isAuthenticationError(error);

  // Network-specific error handling
  if (isNetwork || !networkStatus.isOnline) {
    return (
      <div className={className}>
        <Card className="border-destructive/20 bg-destructive/5">
          <CardContent className="p-4 text-center">
            <Wifi className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium text-destructive mb-2">
              Connection Error
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              {!networkStatus.isOnline
                ? 'No internet connection. Please check your network.'
                : 'Unable to connect to the server. Please try again.'
              }
            </p>
            <ApiRetryButton
              onRetry={onRetry}
              className="w-full"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authentication error handling
  if (isAuth) {
    return (
      <div className={className}>
        <Card className="border-destructive/20 bg-destructive/5">
          <CardContent className="p-4 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
            <p className="text-sm font-medium text-destructive mb-2">
              Authentication Required
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Please sign in again to access corpus files.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if it's a 404 error - show friendly empty state message
  const is404 = error && typeof error === 'object' && 'statusCode' in error && (error as any).statusCode === 404;

  if (is404) {
    return (
      <div className={className}>
        <Card className="border-muted/50">
          <CardContent className="p-4 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium text-muted-foreground mb-2">
              No Corpus Found
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              No documents have been uploaded yet. Upload your first document to get started.
            </p>
            <ApiRetryButton
              onRetry={onRetry}
              className="w-full"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // General error handling
  return (
    <div className={className}>
      <Card className="border-destructive/20 bg-destructive/5">
        <CardContent className="p-4 text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
          <p className="text-sm font-medium text-destructive mb-2">
            Failed to Load Corpus
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            {errorMessage}
          </p>
          <ApiRetryButton
            onRetry={onRetry}
            className="w-full"
          />
        </CardContent>
      </Card>
    </div>
  );
}